/**
 * Signature Generator Utilities & Configuration
 * 
 * This file contains:
 * - COMPANY_CONFIG: Company details, colors, social media links, disclaimer
 * - OFFICE_LOCATIONS: All office addresses (add new offices here + in index.html dropdown)
 * - SOCIAL_ICONS: Base64 encoded social media icons
 * - Utility functions for HTML generation and validation
 * 
 * To customize the signature, modify the constants below.
 */

import { BANNER_BASE64 } from './bannerBase64';

// Interfaces
export interface SignatureData {
    firstName: string;
    lastName: string;
    jobTitle: string;
    mobileNumber: string;
    officeLocation: string;
    entity: string;
}

// Base64 encoded social media icons (embedded for email compatibility)
export const SOCIAL_ICONS = {
    facebook: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAJ1BMVEUAAAAQj88Yj88Vlc8Yk88Wk9IWkdEXlNIWlNIXk9IXlNIXk9IXk9JAjLDxAAAADHRSTlMAECAwQFCAj5+v3+8WV8o4AAAAP0lEQVR42mNABuJ7zgiAGTlnoIw1p6eBaaYzRyFKmM8chDA6z5xoBzPOnDlzEsY4DmaInDkoCFV8gIEIBrITAPXpG21ZhQBwAAAAAElFTkSuQmCC',
    linkedin: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAqElEQVR42mOgCpCYdClBfPLl8+JTLvWTZYD45Ev/YVii/7wCyQaAbIca8F6g/7wAed6YfN6BbM1iky9kwLD2qqtsIG+AwgWExSZfCoBZIDbxYgMIgyzCGQYinTd4RaeeN0ASey825dJ8ZDUgL4MsIWgAuiY0sfXEGzDpYgHI2SBvILuMOAOmXLqP5t33MDmyDADxh6kBj2AYlpCggfUepAE92cPkqJKTAVG7JjV94BiMAAAAAElFTkSuQmCC',
    instagram: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAM1BMVEUAAAAYk88Wk9IXk9IXk9MYktIXktEXlNIWlNIQj88Yj88XlNIXk9IXk9IWkdEVlc8Xk9JXOWRfAAAAEHRSTlMAQFDvz2Bw358QII+vv4AwVhd/EQAAAHxJREFUeNoFwYEBgyAMALC0RdGp0P+vXQIAAPuZ1d3d87phz29lRGYeo3645g049rgwF+JcP7WOQh2cNb5eZKPSriQ6RKPTOeBbotFpffA+dqPT7iArRKMOVr9fPUSjTuz1JI6BawLwvbh7nLH33pGjbriv2d3dXe8GAMAfcXwEsPQRhEIAAAAASUVORK5CYII='
};

// Office locations configuration
export const OFFICE_LOCATIONS: Record<string, { address1: string; address2: string }> = {
    campus: {
        address1: '#1 Jalan Pangkor, Titiwangsa Sentral,',
        address2: '53000 Kuala Lumpur, Malaysia'
    },
    towers: {
        address1: 'Block 19, Star Central, Lingkaran Cyber Point Timur, Cyber 12,',
        address2: '63000 Cyberjaya, Selangor'
    },
    citycentre: {
        address1: 'Level 29 Menara KH, Jalan Sultan Ismail,',
        address2: '50250 Kuala Lumpur, Malaysia'
    },
    penang: {
        address1: 'First Floor, No 17-A, Bangunan Wisma Yeap Chor Ee, Pengkalan Weld,',
        address2: '10300 George Town, Pulau Pinang'
    },
    jakarta_main: {
        address1: 'Jalan IR H Juanda 3, No.22 Jakarta Pusat,',
        address2: '10120 Indonesia'
    },
    jakarta_alam: {
        address1: 'Jalan Jalur Sutera, 29A No.38, Tangerang Selatan,',
        address2: '15325 Indonesia'
    }
};

// Entity options (company names)
export const ENTITIES: Record<string, string> = {
    finexus_sdn: 'FINEXUS Sdn Bhd',
    finexus_international: 'FINEXUS International Sdn Bhd',
    finexus_asia: 'FINEXUS Asia Sdn Bhd',
    finexus_cards: 'FINEXUS Cards Sdn Bhd',
    finexus_group: 'FINEXUS Group'
};

// Default banner (base64 embedded for email compatibility)
export const DEFAULT_BANNER_URL = BANNER_BASE64;

// Hardcoded company details
export const COMPANY_CONFIG = {
    name: 'FINEXUS GROUP',
    website: 'www.finexusgroup.com',
    websiteUrl: 'https://www.finexusgroup.com',
    bannerLink: 'https://www.finexusgroup.com',
    fontStyle: {
        firstColor: '#3080e2',      // FINEXUS Blue
        secondColor: '#878787',     // Gray
        thirdColor: '#2c2c2c'       // Dark gray/black
    },
    socialMedia: {
        linkedin: 'https://www.linkedin.com/company/finexusgroup/',
        facebook: 'https://www.facebook.com/finexusgroupofficial/',
        instagram: 'https://www.instagram.com/finexusgroup/'
    },
    disclaimer: `This email (and attachments) is confidential and intended for the named recipient only; if received in error, please notify the sender and delete it. No person is authorised to enter into, amend, or accept contracts or legal obligations for any FINEXUS Group entity via email unless confirmed in a signed written agreement. Service of legal documents by email is not accepted unless required by law or agreed in writing. Emails may contain viruses, malicious code, corruption, or be intercepted - FINEXUS bears no liability and recipients should scan all communications. Our email servers may be outside your jurisdiction; by sending personal data, you confirm all necessary legal consents have been obtained.`
};

/**
 * Escape HTML special characters to prevent XSS
 */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Validate that all required fields are filled
 */
export function isFormDataValid(data: SignatureData): boolean {
    return !!(data.firstName && data.lastName && data.jobTitle && 
             data.mobileNumber && data.officeLocation && data.entity);
}

/**
 * Get missing fields from form data
 */
export function getMissingFields(data: SignatureData): string[] {
    const missingFields: string[] = [];
    if (!data.firstName) missingFields.push('First Name');
    if (!data.lastName) missingFields.push('Last Name');
    if (!data.jobTitle) missingFields.push('Job Title');
    if (!data.mobileNumber) missingFields.push('Mobile Number');
    if (!data.officeLocation) missingFields.push('Office Location');
    if (!data.entity) missingFields.push('Entity');
    return missingFields;
}

/**
 * Format name to uppercase
 */
export function formatFullName(firstName: string, lastName: string): string {
    return `${firstName.toUpperCase()} ${lastName.toUpperCase()}`;
}

/**
 * Create social media links HTML
 */
export function createSocialLinksHtml(): string {
    const links: Array<{url: string; iconUrl: string; name: string}> = [];
    
    if (COMPANY_CONFIG.socialMedia.facebook) {
        links.push({ 
            url: COMPANY_CONFIG.socialMedia.facebook, 
            iconUrl: SOCIAL_ICONS.facebook,
            name: 'Facebook'
        });
    }
    if (COMPANY_CONFIG.socialMedia.linkedin) {
        links.push({ 
            url: COMPANY_CONFIG.socialMedia.linkedin, 
            iconUrl: SOCIAL_ICONS.linkedin,
            name: 'LinkedIn'
        });
    }
    if (COMPANY_CONFIG.socialMedia.instagram) {
        links.push({ 
            url: COMPANY_CONFIG.socialMedia.instagram, 
            iconUrl: SOCIAL_ICONS.instagram,
            name: 'Instagram'
        });
    }

    if (links.length === 0) return '';

    const iconCells = links.map((link, index) => {
        const paddingLeft = index === 0 ? '0' : '8px';
        return `<td style="padding: 0; padding-left: ${paddingLeft}; vertical-align: middle;"><a href="${link.url}" target="_blank" style="text-decoration: none !important; border-bottom: none;" title="${link.name}"><img src="${link.iconUrl}" alt="${link.name}" width="16" height="16" style="border: 0; display: block; width: 16px; height: 16px;" /></a></td>`;
    }).join('');

    return `<table cellpadding="0" cellspacing="0" border="0" style="margin-left: 4px;"><tr>${iconCells}</tr></table>`;
}

/**
 * Create reply signature HTML (minimal version with name and job title only)
 */
export function createReplySignatureHtml(data: SignatureData): string {
    const fullName = formatFullName(data.firstName, data.lastName);
    
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor}; line-height: 1.5;">
<tr>
<td style="padding: 0; padding-bottom: 4px; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor};">Regards,</td>
</tr>
<tr>
<td style="padding: 0; font-size: 12pt; font-weight: bold; color: ${COMPANY_CONFIG.fontStyle.firstColor};">${escapeHtml(fullName)}</td>
</tr>
<tr>
<td style="padding: 0; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.secondColor};">${escapeHtml(data.jobTitle.toUpperCase())}</td>
</tr>
</table>`;
}

/**
 * Create full signature HTML
 */
export function createSignatureHtml(data: SignatureData): string {
    const fullName = formatFullName(data.firstName, data.lastName);
    const officeAddress = OFFICE_LOCATIONS[data.officeLocation];
    const entityName = ENTITIES[data.entity];
    
    if (!officeAddress) {
        throw new Error(`Invalid office location: ${data.officeLocation}`);
    }
    
    if (!entityName) {
        throw new Error(`Invalid entity: ${data.entity}`);
    }
    
    // Segment 1: Contact info (750px)
    const contactSection = `<table cellpadding="0" cellspacing="0" border="0" width="750" style="font-family: Arial, sans-serif; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor}; line-height: 1.5;">
<tr>
<td style="padding: 0; padding-right: 40px; font-size: 12pt; font-weight: bold; color: ${COMPANY_CONFIG.fontStyle.firstColor};">${escapeHtml(fullName)}</td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; padding-bottom: 6px; border-bottom: 1px solid ${COMPANY_CONFIG.fontStyle.firstColor}; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.secondColor};">${escapeHtml(data.jobTitle.toUpperCase())}</td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; padding-top: 6px; font-size: 10pt; font-weight: bold; color: ${COMPANY_CONFIG.fontStyle.thirdColor};">${escapeHtml(entityName)}</td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor};">${escapeHtml(officeAddress.address1)}</td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor};">${escapeHtml(officeAddress.address2)}</td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; font-size: 10pt; color: ${COMPANY_CONFIG.fontStyle.thirdColor};"><strong>m:</strong> <a href="tel:${data.mobileNumber.replace(/\s/g, '')}" style="color: ${COMPANY_CONFIG.fontStyle.firstColor}; text-decoration: none !important; border-bottom: none; font-size: 10pt;">${escapeHtml(data.mobileNumber)}</a></td>
</tr>
<tr>
<td style="padding: 0; padding-right: 40px; padding-top: 6px;">
<table cellpadding="0" cellspacing="0" border="0" width="710">
<tr>
<td align="left" valign="middle" style="padding: 0; font-size: 10pt;"><a href="${COMPANY_CONFIG.websiteUrl}" target="_blank" style="color: ${COMPANY_CONFIG.fontStyle.firstColor}; font-weight: bold; text-decoration: none !important; border-bottom: none; font-size: 10pt;">${escapeHtml(COMPANY_CONFIG.website)}</a></td>
<td align="right" valign="middle" style="padding: 0;">${createSocialLinksHtml()}</td>
</tr>
</table>
</td>
</tr>
</table>`;

    // Segment 2: Banner + Disclaimer (750px full width, no padding)
    const bannerSection = `<table cellpadding="0" cellspacing="0" border="0" width="750" style="font-family: Arial, sans-serif;">
<tr>
<td style="padding: 0; padding-top: 10px;">
<a href="${COMPANY_CONFIG.bannerLink}" target="_blank" style="text-decoration: none;">
<img src="${DEFAULT_BANNER_URL}" alt="${COMPANY_CONFIG.name}" width="750" height="116" style="display: block; border: 0; width: 750px; height: 116px; max-width: 750px;" />
</a>
</td>
</tr>
<tr>
<td style="padding: 0; padding-top: 10px; font-size: 7pt; color: ${COMPANY_CONFIG.fontStyle.secondColor}; line-height: 1.4;">${escapeHtml(COMPANY_CONFIG.disclaimer)}</td>
</tr>
</table>`;

    return contactSection + bannerSection;
}
